import React, { useEffect, useState } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Placeholder from "@tiptap/extension-placeholder"
import Link from "@tiptap/extension-link"
import TextAlign from "@tiptap/extension-text-align"
import Underline from "@tiptap/extension-underline"
import Image from "@tiptap/extension-image"
import { theme, Button, Tooltip, Select, Modal, Input, Dropdown } from "antd"
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  DisconnectOutlined,
  UndoOutlined,
  RedoOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  BlockOutlined,
  MinusOutlined,
  CodeOutlined,
  ClearOutlined,
  PictureOutlined,
} from "@ant-design/icons"
import type { RichTextEditorProps } from "./RichTextEditor.types"
import "./RichTextEditor.css"

const HEADING_OPTIONS = [
  { value: "p", label: "Paragraph" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
]

export const RichTextEditor: React.FunctionComponent<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  minHeight = 200,
  hasError = false,
  disableImages = false,
  showVariableInsert = false,
  variableItems = [],
}) => {
  const { token } = theme.useToken()
  const [isFocused, setIsFocused] = useState(false)
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const [imageAlt, setImageAlt] = useState("")

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder ?? "Write content..." }),
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: { class: "rte-image" },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      onChange(html === "<p></p>" ? "" : html)
    },
  })

  // Sync external value changes (e.g. form reset, loading existing data)
  // without triggering an onChange loop — only update when the editor
  // content actually differs from the incoming value.
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    const incoming = value ?? ""
    if (current !== incoming) {
      editor.commands.setContent(incoming, false)
    }
  }, [editor, value])

  const borderColor = hasError ? token.colorError : token.colorBorder
  const focusColor = hasError ? token.colorError : token.colorPrimary
  const activeBorderColor = isFocused ? focusColor : borderColor
  const activeBoxShadow = isFocused ? `0 0 0 2px ${focusColor}26` : "none"

  const headingValue = (() => {
    if (!editor) return "p"
    if (editor.isActive("heading", { level: 1 })) return "h1"
    if (editor.isActive("heading", { level: 2 })) return "h2"
    if (editor.isActive("heading", { level: 3 })) return "h3"
    return "p"
  })()

  const openLinkModal = () => {
    if (!editor) return
    // Pre-populate with current link href if cursor is on a link
    const existingHref = editor.getAttributes("link").href ?? ""
    setLinkUrl(existingHref)
    setLinkModalOpen(true)
  }

  const confirmLink = () => {
    if (!editor) return
    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl })
        .run()
    }
    setLinkModalOpen(false)
    setLinkUrl("")
  }

  const cancelLink = () => {
    setLinkModalOpen(false)
    setLinkUrl("")
  }

  const openImageModal = () => {
    if (!editor) return
    setImageUrl("")
    setImageAlt("")
    setImageModalOpen(true)
  }

  const confirmImage = () => {
    if (!editor || !imageUrl) return
    const trimmed = imageUrl.trim()
    try {
      const parsed = new URL(trimmed)
      if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return
    } catch {
      return
    }
    editor
      .chain()
      .focus()
      .setImage({ src: trimmed, alt: imageAlt.trim() })
      .run()
    setImageModalOpen(false)
    setImageUrl("")
    setImageAlt("")
  }

  const cancelImage = () => {
    setImageModalOpen(false)
    setImageUrl("")
    setImageAlt("")
  }

  return (
    <div
      style={{
        position: "relative",
        border: `1px solid ${activeBorderColor}`,
        borderRadius: token.borderRadius,
        overflow: "hidden",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: activeBoxShadow,
      }}
      className="tiptap-wrapper"
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <div
        tabIndex={disabled ? -1 : 0}
        aria-label="Focus editor content"
        onFocus={() => editor?.commands.focus()}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          padding: "6px 8px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorFillAlter,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Heading selector */}
        <Select
          size="small"
          value={headingValue}
          disabled={disabled}
          popupMatchSelectWidth={false}
          options={HEADING_OPTIONS}
          style={{ width: "auto", minWidth: 100 }}
          onChange={(val) => {
            if (val === "p") {
              editor?.chain().focus().setParagraph().run()
            } else {
              editor
                ?.chain()
                .focus()
                .setHeading({ level: Number(val[1]) as 1 | 2 | 3 })
                .run()
            }
          }}
        />
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* Text formatting */}
        <Tooltip title="Bold">
          <Button
            type={editor?.isActive("bold") ? "primary" : "text"}
            size="small"
            icon={<BoldOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleBold().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Italic">
          <Button
            type={editor?.isActive("italic") ? "primary" : "text"}
            size="small"
            icon={<ItalicOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleItalic().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Underline">
          <Button
            type={editor?.isActive("underline") ? "primary" : "text"}
            size="small"
            icon={<UnderlineOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleMark("underline").run()
            }}
          />
        </Tooltip>
        <Tooltip title="Strikethrough">
          <Button
            type={editor?.isActive("strike") ? "primary" : "text"}
            size="small"
            icon={<StrikethroughOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleStrike().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Inline code">
          <Button
            type={editor?.isActive("code") ? "primary" : "text"}
            size="small"
            icon={<CodeOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleCode().run()
            }}
          />
        </Tooltip>
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* Lists */}
        <Tooltip title="Bullet list">
          <Button
            type={editor?.isActive("bulletList") ? "primary" : "text"}
            size="small"
            icon={<UnorderedListOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleBulletList().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Ordered list">
          <Button
            type={editor?.isActive("orderedList") ? "primary" : "text"}
            size="small"
            icon={<OrderedListOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleOrderedList().run()
            }}
          />
        </Tooltip>
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* Alignment */}
        <Tooltip title="Align left">
          <Button
            type={editor?.isActive({ textAlign: "left" }) ? "primary" : "text"}
            size="small"
            icon={<AlignLeftOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().setTextAlign("left").run()
            }}
          />
        </Tooltip>
        <Tooltip title="Align center">
          <Button
            type={
              editor?.isActive({ textAlign: "center" }) ? "primary" : "text"
            }
            size="small"
            icon={<AlignCenterOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().setTextAlign("center").run()
            }}
          />
        </Tooltip>
        <Tooltip title="Align right">
          <Button
            type={editor?.isActive({ textAlign: "right" }) ? "primary" : "text"}
            size="small"
            icon={<AlignRightOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().setTextAlign("right").run()
            }}
          />
        </Tooltip>
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* Blockquote + horizontal rule */}
        <Tooltip title="Blockquote">
          <Button
            type={editor?.isActive("blockquote") ? "primary" : "text"}
            size="small"
            icon={<BlockOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().toggleBlockquote().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Horizontal rule">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().setHorizontalRule().run()
            }}
          />
        </Tooltip>
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* Image */}
        {!disableImages && (
          <Tooltip title="Insert image">
            <Button
              type="text"
              size="small"
              icon={<PictureOutlined />}
              disabled={disabled}
              onMouseDown={(e) => {
                e.preventDefault()
                openImageModal()
              }}
            />
          </Tooltip>
        )}
        {/* Link */}
        <Tooltip title="Insert link">
          <Button
            type={editor?.isActive("link") ? "primary" : "text"}
            size="small"
            icon={<LinkOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              openLinkModal()
            }}
          />
        </Tooltip>
        {editor?.isActive("link") && (
          <Tooltip title="Remove link">
            <Button
              type="text"
              size="small"
              icon={<DisconnectOutlined />}
              disabled={disabled}
              onMouseDown={(e) => {
                e.preventDefault()
                editor
                  ?.chain()
                  .focus()
                  .extendMarkRange("link")
                  .unsetLink()
                  .run()
              }}
            />
          </Tooltip>
        )}
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        {/* History */}
        <Tooltip title="Undo">
          <Button
            type="text"
            size="small"
            icon={<UndoOutlined />}
            disabled={disabled || !editor?.can().undo()}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().undo().run()
            }}
          />
        </Tooltip>
        <Tooltip title="Redo">
          <Button
            type="text"
            size="small"
            icon={<RedoOutlined />}
            disabled={disabled || !editor?.can().redo()}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().redo().run()
            }}
          />
        </Tooltip>
        <span
          style={{
            width: 1,
            alignSelf: "stretch",
            background: token.colorBorderSecondary,
            margin: "2px 4px",
          }}
        />
        <Tooltip title="Clear formatting">
          <Button
            type="text"
            size="small"
            icon={<ClearOutlined />}
            disabled={disabled}
            onMouseDown={(e) => {
              e.preventDefault()
              editor?.chain().focus().unsetAllMarks().run()
            }}
          />
        </Tooltip>
        {showVariableInsert && (
          <>
            <span
              style={{
                width: 1,
                alignSelf: "stretch",
                background: token.colorBorderSecondary,
                margin: "2px 4px",
              }}
            />
            <Dropdown
              menu={{
                items: variableItems.map((v) => ({
                  key: v.key,
                  label: v.label,
                })),
                onClick: ({ key }) => {
                  editor?.chain().focus().insertContent(`{{${key}}}`).run()
                },
              }}
              trigger={["click"]}
              disabled={disabled}
            >
              <Button
                type="text"
                size="small"
                icon={<CodeOutlined />}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
              >
                Variable
              </Button>
            </Dropdown>
          </>
        )}
      </div>
      <div
        style={{
          padding: "8px 12px",
          minHeight,
          cursor: disabled ? "not-allowed" : "text",
          color: token.colorText,
          fontSize: token.fontSize,
          lineHeight: token.lineHeight,
        }}
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>
      <Modal
        title="Insert Link"
        open={linkModalOpen}
        onOk={confirmLink}
        onCancel={cancelLink}
        okText="Apply"
        width={400}
      >
        <Input
          placeholder="https://example.com"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={confirmLink}
          autoFocus
        />
      </Modal>
      <Modal
        title="Insert Image"
        open={imageModalOpen}
        onOk={confirmImage}
        onCancel={cancelImage}
        okText="Insert"
        okButtonProps={{ disabled: !imageUrl }}
        width={480}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            paddingTop: 8,
          }}
        >
          <Input
            placeholder="https://example.com/image.png"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onPressEnter={confirmImage}
            autoFocus
          />
          <Input
            placeholder="Alt text (recommended)"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  )
}
